import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Link from '@mui/material/Link';

import { adUrl, adsetUrl, campaignUrl } from './adsManager';
import { SiteLink, TemplateSummary } from './types';

/** A name that opens the object in Ads Manager. */
function AdsManagerLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <Link href={href} target="_blank" rel="noopener" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      {children}
      <OpenInNewIcon sx={{ fontSize: 13 }} />
    </Link>
  );
}

/** "click 7d + view 1d" — the attribution window in one line. */
function attributionLabel(spec: { event_type: string; window_days: number }[] | null | undefined) {
  if (!spec || !spec.length) return '—';
  return spec
    .map(s => `${s.event_type.replace('_THROUGH', '').replace('ENGAGED_VIDEO_VIEW', 'video view').toLowerCase()} ${s.window_days}d`)
    .join(' + ');
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction="row" spacing={1} sx={{ py: 0.25, alignItems: 'baseline' }}>
      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 122, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{children}</Typography>
    </Stack>
  );
}

/**
 * Everything about one template: what it does, where it came from, and its
 * site-link curation.
 *
 * Two templates look identical by name and campaign; what distinguishes them is
 * entirely in the settings — Cooling Comforter optimises OFFSITE_CONVERSIONS on
 * a 7d-click/1d-view window, DCOP optimises VALUE on 7d-click alone.
 */
export default function TemplateDetail({
  template,
  accountId,
  minSiteLinks,
  busy,
  onPatch,
  onRecapture,
  onResync,
  onDelete,
}: {
  template: TemplateSummary;
  accountId: string;
  minSiteLinks: number;
  busy: boolean;
  onPatch: (patch: { name?: string; siteLinksEnabled?: boolean; siteLinksSelected?: string[] }) => void;
  onRecapture: () => void;
  onResync: () => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(template.name ?? '');

  const a = template.adset;
  const c = template.creative;
  const t = a?.targeting;
  const interests = t?.flexible_spec?.[0]?.interests ?? [];

  // A null selection means "everything captured".
  const selected = new Set(template.siteLinksSelected ?? template.siteLinks.map(l => l.url));
  const count = template.siteLinks.filter(l => selected.has(l.url)).length;
  const invalid = template.siteLinksEnabled && count > 0 && count < minSiteLinks;

  const toggleLink = (link: SiteLink) => {
    const next = new Set(selected);
    if (next.has(link.url)) next.delete(link.url);
    else next.add(link.url);
    onPatch({ siteLinksSelected: [...next] });
  };

  const onProduct = template.siteLinks.filter(l => l.matchesDestination);
  const elsewhere = template.siteLinks.filter(l => !l.matchesDestination);

  const linkRow = (link: SiteLink) => (
    <Stack key={`${link.title}|${link.url}`} direction="row" alignItems="center" spacing={1}>
      <Checkbox size="small" sx={{ p: 0.5 }} checked={selected.has(link.url)} onChange={() => toggleLink(link)} disabled={busy} />
      <Typography variant="body2" sx={{ minWidth: 200 }} noWrap title={link.title}>
        {link.title || '(no title)'}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }} noWrap>{link.path || '—'}</Typography>
      {link.empty && (
        <Tooltip title="No destination URL. Meta suppresses links like this.">
          <Chip size="small" color="error" variant="outlined" label="no URL" />
        </Tooltip>
      )}
    </Stack>
  );

  return (
    <Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Ad set</Typography>
          {a ? (
            <>
              <Row label="Optimising for">{a.optimization_goal}{a.billing_event ? ` · billed on ${a.billing_event.toLowerCase()}` : ''}</Row>
              <Row label="Attribution">{attributionLabel(a.attribution_spec)}</Row>
              <Row label="Conversion">
                {a.promoted_object
                  ? `${a.promoted_object.custom_event_str || a.promoted_object.custom_event_type || '—'} · pixel ${a.promoted_object.pixel_id}`
                  : '—'}
              </Row>
              <Row label="Audience">
                {t?.geo_locations?.countries?.join(', ') || '—'} · ages {t?.age_min ?? '?'}–{t?.age_max ?? '?'}
                {t?.targeting_automation?.advantage_audience === 0 ? ' · Advantage audience off' : ''}
              </Row>
              {interests.length > 0 && <Row label="Interests">{interests.map(i => i.name).join(', ')}</Row>}
              {(t?.excluded_custom_audiences?.length ?? 0) > 0 && (
                <Row label="Excluding">{t!.excluded_custom_audiences!.length} custom audiences</Row>
              )}
            </>
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>Captured before settings were recorded — re-sync to see them.</Alert>
          )}
        </Box>

        <Box>
          <Typography variant="caption" sx={{ fontWeight: 700 }}>Ad</Typography>
          {c && (
            <>
              <Row label="Sends people to">{c.link}</Row>
              <Row label="Button">{c.call_to_action_type}</Row>
              <Row label="Posting as">
                page {c.page_id}{c.instagram_user_id ? ` · Instagram ${c.instagram_user_id}` : ' · no Instagram account'}
              </Row>
              <Row label="UTM tags">{c.url_tags || '—'}</Row>
            </>
          )}
          <Row label="Site links">
            {template.siteLinksEnabled ? `${count} attached` : `off — ${template.siteLinks.length} captured, none sent`}
          </Row>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      <Typography variant="caption" sx={{ fontWeight: 700 }}>Built from</Typography>
      <Row label="Campaign">
        {template.campaignId
          ? <AdsManagerLink href={campaignUrl(accountId, template.campaignId)}>{template.campaignName}</AdsManagerLink>
          : template.campaignName}
      </Row>
      <Row label="Ad set">
        {template.sourceAdset?.id
          ? <AdsManagerLink href={adsetUrl(accountId, template.sourceAdset.id)}>{template.sourceAdset.name}</AdsManagerLink>
          : '—'}
        {template.sourceAdset?.createdTime ? ` · created ${new Date(template.sourceAdset.createdTime).toLocaleDateString()}` : ''}
      </Row>
      <Row label="Ad">
        {template.sourceAd?.id
          ? <AdsManagerLink href={adUrl(accountId, template.sourceAd.id)}>{template.sourceAd.name}</AdsManagerLink>
          : '—'}
        {template.sourceAd && !template.sourceAd.exactKind
          ? ` · a ${template.sourceAd.mediaKind} ad (this campaign had none of this medium; page, link and CTA are the same either way)`
          : ''}
      </Row>
      <Row label="Last synced">{template.capturedAt ? new Date(template.capturedAt).toLocaleString() : '—'}</Row>

      <Divider sx={{ my: 2 }} />

      <FormControlLabel
        control={
          <Switch size="small" checked={template.siteLinksEnabled} onChange={e => onPatch({ siteLinksEnabled: e.target.checked })} disabled={busy} />
        }
        label="Send site links with these ads"
      />
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
        Off by default. Site links show on Facebook Feed only, and the set captured from an existing ad
        usually includes other products&rsquo; pages.
      </Typography>

      {invalid && (
        <Alert severity="error" sx={{ mb: 1 }}>
          Meta needs at least {minSiteLinks} site links, or none. {count} selected.
        </Alert>
      )}

      {template.siteLinksEnabled && template.siteLinks.length === 0 && (
        <Alert severity="info" sx={{ mb: 1 }}>No site links were captured. Re-sync to pick them up.</Alert>
      )}

      {template.siteLinksEnabled && template.siteLinks.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {onProduct.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>Same destination as the ad ({onProduct.length})</Typography>
              {onProduct.map(linkRow)}
            </>
          )}
          {elsewhere.length > 0 && (
            <>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mt: 1 }}>Somewhere else ({elsewhere.length})</Typography>
              {elsewhere.map(linkRow)}
            </>
          )}
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          size="small"
          label="Name"
          value={name}
          onChange={e => setName(e.target.value)}
          onBlur={() => { if (name.trim() && name !== template.name) onPatch({ name: name.trim() }); }}
          sx={{ minWidth: 240 }}
        />
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Re-read the exact ad set and ad above. Use this when you have edited that ad set in Ads Manager and want the template to match it again.">
          <span><Button size="small" variant="outlined" onClick={onResync} disabled={busy}>Re-sync from source</Button></span>
        </Tooltip>
        <Tooltip title="Take the campaign's NEWEST ad set and ad instead. Use this when the reference has moved on — note that after a few launches the newest batch is one this tool created.">
          <span><Button size="small" onClick={onRecapture} disabled={busy}>Re-capture from campaign</Button></span>
        </Tooltip>
        <Button size="small" color="error" onClick={onDelete} disabled={busy}>Delete</Button>
      </Stack>
    </Box>
  );
}
